import React, { useEffect, useState } from 'react';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useUser } from '../../contexts/UserContext';
import DiscordIdentityPanel from '../components/DiscordIdentityPanel';
import YouTubeIdentityPanel from '../components/YouTubeIdentityPanel';
import MyJoinRequestsPanel from '../components/MyJoinRequestsPanel';
import MyClaimOpportunitiesPanel from '../components/MyClaimOpportunitiesPanel';
import MyReportedIssuesPanel from '../components/support/MyReportedIssuesPanel';
import ReportIssueTrigger from '../components/support/ReportIssueTrigger';
import LoadingState from '../components/LoadingState';
import { lscReturnTo } from '../routing';
import LscInstallBanner from '../components/LscInstallBanner';
import { useLscContentColumn } from '../lscContentColumn';

const LscAccountSettingsPage: React.FC = () => {
  useLscContentColumn('narrow');
  return (
    <LscAuthGate returnTo={lscReturnTo('/settings')}>
      <AccountSettingsContent />
    </LscAuthGate>
  );
};

function AccountSettingsContent() {
  const { user, loading, refreshUser } = useUser();
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void refreshUser().finally(() => {
      if (!cancelled) setSessionReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  if (loading || !sessionReady) {
    return <LoadingState message="Loading account settings..." />;
  }

  if (!user) return null;

  const twitchLabel = user.display_name || user.twitch_login || 'Twitch account';

  return (
    <div className="space-y-6 max-w-2xl" data-testid="lsc-account-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Account settings</h1>
        <p className="text-sm text-secondary-400">
          Manage your Stream Center identities. These settings apply to your account across all
          league workspaces.
        </p>
      </div>

      <LscInstallBanner variant="section" />

      <div className="card space-y-3 text-sm" data-testid="twitch-identity-summary">
        <h2 className="font-semibold text-white">Twitch identity</h2>
        <div className="flex items-center gap-3">
          {user.profile_image_url ? (
            <img src={user.profile_image_url} alt="" className="h-10 w-10 rounded-full" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-secondary-700" />
          )}
          <div>
            <p className="text-white font-medium">{twitchLabel}</p>
            {user.twitch_login && (
              <p className="text-secondary-400">@{user.twitch_login}</p>
            )}
          </div>
        </div>
        <p className="text-secondary-500 text-xs">
          Twitch sign-in is required for Stream Center. Disconnect is not available here.
        </p>
      </div>

      <DiscordIdentityPanel returnTo={lscReturnTo('/settings')} />

      <YouTubeIdentityPanel returnTo={lscReturnTo('/settings')} />

      <MyJoinRequestsPanel />

      <MyClaimOpportunitiesPanel compactEmpty />

      <div className="card space-y-3 text-sm" data-testid="lsc-account-help-section">
        <h2 className="font-semibold text-white">Help</h2>
        <p className="text-secondary-400 text-xs">
          Something not working? Report an issue and our team will investigate. Reports do not change
          your league data.
        </p>
        <ReportIssueTrigger
          variant="inline"
          testId="lsc-account-report-issue"
          prefill={{ surface: 'account_settings', category: 'other' }}
        />
      </div>

      <MyReportedIssuesPanel />

      {user.identities && user.identities.length > 0 && (
        <div className="card space-y-3 text-sm" data-testid="connected-identities-summary">
          <h2 className="font-semibold text-white">Connected identities</h2>
          <ul className="space-y-2">
            {user.identities.map((identity) => (
              <li
                key={`${identity.provider}-${identity.provider_user_id || identity.username}`}
                className="flex justify-between gap-4 text-secondary-300"
              >
                <span className="capitalize">{identity.provider}</span>
                <span className="text-secondary-400 truncate">
                  {identity.username || identity.display_name || identity.provider_user_id || '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-sm text-secondary-500">
        Connecting Discord helps commissioners match you to imported NeonSportz roster slots after
        you join a league. Use an invite link or enter a code on the join page to request access.
      </p>
    </div>
  );
}

export default LscAccountSettingsPage;
